import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Trash2,
  Edit2,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  MapPin,
  Check
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Calendars = () => {
  const { isAdmin, currentOrganization } = useAuth();
  const { t, language } = useLanguage();
  const [calendars, setCalendars] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [settings, setSettings] = useState({ week_starts_monday: true });
  
  // Calendar form
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [calendarForm, setCalendarForm] = useState({
    name: '',
    year: new Date().getFullYear(),
    is_default: false
  });

  // Holiday form
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [holidayForm, setHolidayForm] = useState({
    calendar_id: '',
    name: '',
    date: '',
    is_national: true
  });

  // BOE Import state
  const [boeDialogOpen, setBoeDialogOpen] = useState(false);
  const [boeLoading, setBoeLoading] = useState(false);
  const [boeImporting, setBoeImporting] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [boeForm, setBoeForm] = useState({
    year: new Date().getFullYear(),
    community_code: '',
    include_nationals: true
  });
  const [boePreview, setBoePreview] = useState(null);

  // Reset data when organization changes
  useEffect(() => {
    setCalendars([]);
    setHolidays([]);
    setSelectedCalendar(null);
    setLoading(true);
  }, [currentOrganization?.id]);

  useEffect(() => {
    fetchCalendars();
    fetchSettings();
    fetchCommunities();
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (selectedCalendar) {
      fetchHolidays(selectedCalendar.id);
    }
  }, [selectedCalendar]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.log('Using default settings');
    }
  };

  const fetchCommunities = async () => {
    try {
      const response = await axios.get(`${API_URL}/calendars/boe/communities`);
      setCommunities(response.data);
    } catch (error) {
      console.log('Error fetching communities');
    }
  };

  const fetchCalendars = async () => {
    try {
      const response = await axios.get(`${API_URL}/calendars`);
      setCalendars(response.data);
      if (response.data.length > 0 && !selectedCalendar) {
        setSelectedCalendar(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching calendars:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async (calendarId) => {
    try {
      const response = await axios.get(`${API_URL}/calendars/${calendarId}/holidays`);
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const handleCalendarSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCalendar) {
        await axios.put(`${API_URL}/calendars/${editingCalendar.id}`, calendarForm);
        toast.success(language === 'es' ? 'Calendario actualizado' : 'Calendar updated');
      } else {
        await axios.post(`${API_URL}/calendars`, calendarForm);
        toast.success(language === 'es' ? 'Calendario creado' : 'Calendar created');
      }
      fetchCalendars();
      setCalendarDialogOpen(false);
      setEditingCalendar(null);
      setCalendarForm({ name: '', year: new Date().getFullYear(), is_default: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleEditCalendar = (calendar) => {
    setEditingCalendar(calendar);
    setCalendarForm({
      name: calendar.name,
      year: calendar.year,
      is_default: calendar.is_default || false
    });
    setCalendarDialogOpen(true);
  };

  const handleDeleteCalendar = async (calendarId) => {
    if (!window.confirm(language === 'es' 
      ? '¿Eliminar este calendario? Se eliminarán todos los festivos asociados.' 
      : 'Delete this calendar? All associated holidays will be deleted.')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/calendars/${calendarId}`);
      toast.success(language === 'es' ? 'Calendario eliminado' : 'Calendar deleted');
      if (selectedCalendar?.id === calendarId) {
        setSelectedCalendar(null);
        setHolidays([]);
      }
      fetchCalendars();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleHolidaySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/calendars/holidays`, {
        ...holidayForm,
        calendar_id: selectedCalendar.id
      });
      fetchHolidays(selectedCalendar.id);
      setHolidayDialogOpen(false);
      setHolidayForm({ calendar_id: '', name: '', date: '', is_national: true });
      toast.success(language === 'es' ? 'Festivo añadido' : 'Holiday added');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (window.confirm(t('confirm_delete'))) {
      try {
        await axios.delete(`${API_URL}/calendars/holidays/${id}`);
        fetchHolidays(selectedCalendar.id);
        toast.success(language === 'es' ? 'Festivo eliminado' : 'Holiday deleted');
      } catch (error) {
        toast.error(error.response?.data?.detail || t('error_occurred'));
      }
    }
  };

  // BOE Import functions
  const handleOpenBoeDialog = () => {
    setBoeForm({
      year: selectedCalendar?.year || new Date().getFullYear(),
      community_code: '',
      include_nationals: true
    });
    setBoePreview(null);
    setBoeDialogOpen(true);
  };

  const handleBoePreview = async () => {
    if (!boeForm.year) {
      toast.error(language === 'es' ? 'Selecciona un año' : 'Select a year');
      return;
    }
    
    setBoeLoading(true);
    try {
      const params = new URLSearchParams({
        include_nationals: boeForm.include_nationals
      });
      if (boeForm.community_code) {
        params.append('community_code', boeForm.community_code);
      }
      
      const response = await axios.get(
        `${API_URL}/calendars/boe/preview/${boeForm.year}?${params}`
      );
      setBoePreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' 
        ? 'Error al obtener festivos del BOE' 
        : 'Error fetching BOE holidays'));
    } finally {
      setBoeLoading(false);
    }
  };

  const handleBoeImport = async () => {
    if (!selectedCalendar) {
      toast.error(language === 'es' ? 'Selecciona un calendario' : 'Select a calendar');
      return;
    }
    
    setBoeImporting(true);
    try {
      const response = await axios.post(`${API_URL}/calendars/boe/import`, {
        calendar_id: selectedCalendar.id,
        year: boeForm.year,
        community_code: boeForm.community_code || null,
        include_nationals: boeForm.include_nationals
      });
      
      toast.success(language === 'es' 
        ? `${response.data.imported} festivos importados correctamente` 
        : `${response.data.imported} holidays imported successfully`);
      
      fetchHolidays(selectedCalendar.id);
      setBoeDialogOpen(false);
      setBoePreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' 
        ? 'Error al importar festivos' 
        : 'Error importing holidays'));
    } finally {
      setBoeImporting(false);
    }
  };

  // Calendar rendering helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Adjust for week start (Monday = 0 if week starts monday, Sunday = 0 otherwise)
    let startingDay;
    if (settings.week_starts_monday) {
      startingDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    } else {
      startingDay = firstDay.getDay();
    }
    
    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const isHoliday = (day) => {
    if (!day) return false;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidays.find(h => h.date === dateStr);
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const dayNames = settings.week_starts_monday
    ? (language === 'es'
        ? ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
        : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'])
    : (language === 'es'
        ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendars-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('calendars')}</h1>
        <p className="text-slate-500">
          {language === 'es' ? 'Gestión de calendarios laborales y festivos' : 'Manage work calendars and holidays'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar List */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Manrope'] text-lg">{t('calendars')}</CardTitle>
              {isAdmin() && (
                <Dialog open={calendarDialogOpen} onOpenChange={(open) => {
                  setCalendarDialogOpen(open);
                  if (!open) {
                    setEditingCalendar(null);
                    setCalendarForm({ name: '', year: new Date().getFullYear(), is_default: false });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-['Manrope']">
                        {editingCalendar 
                          ? (language === 'es' ? 'Editar Calendario' : 'Edit Calendar')
                          : (language === 'es' ? 'Nuevo Calendario' : 'New Calendar')
                        }
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCalendarSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{t('name')}</Label>
                        <Input
                          value={calendarForm.name}
                          onChange={(e) => setCalendarForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder={language === 'es' ? 'Ej: Calendario 2025' : 'E.g.: Calendar 2025'}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'es' ? 'Año' : 'Year'}</Label>
                        <Input
                          type="number"
                          value={calendarForm.year}
                          onChange={(e) => setCalendarForm(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                          min="2020"
                          max="2050"
                          required
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={calendarForm.is_default}
                          onCheckedChange={(checked) => setCalendarForm(prev => ({ ...prev, is_default: checked }))}
                        />
                        <Label>{language === 'es' ? 'Calendario por defecto' : 'Default calendar'}</Label>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setCalendarDialogOpen(false)}>
                          {t('cancel')}
                        </Button>
                        <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                          {editingCalendar ? t('update') : t('save')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {calendars.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                {language === 'es' ? 'No hay calendarios. Crea uno para empezar.' : 'No calendars. Create one to start.'}
              </p>
            ) : (
              <div className="space-y-2">
                {calendars.map(calendar => (
                  <div
                    key={calendar.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedCalendar?.id === calendar.id 
                        ? 'bg-indigo-50 border-indigo-300' 
                        : 'bg-white border-slate-200 hover:border-indigo-200'
                    }`}
                    onClick={() => setSelectedCalendar(calendar)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-slate-900">{calendar.name}</span>
                        <span className="text-sm text-slate-500 ml-2">({calendar.year})</span>
                        {calendar.is_default && (
                          <Badge className="ml-2 bg-indigo-100 text-indigo-700">
                            {language === 'es' ? 'Por defecto' : 'Default'}
                          </Badge>
                        )}
                      </div>
                      {isAdmin() && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCalendar(calendar);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCalendar(calendar.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calendar View */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-indigo-600" />
                {selectedCalendar?.name || (language === 'es' ? 'Selecciona un calendario' : 'Select a calendar')}
              </CardTitle>
              {selectedCalendar && (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={prevMonth}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[120px] text-center">
                    {currentMonth.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { month: 'long', year: 'numeric' })}
                  </span>
                  <Button variant="ghost" size="icon" onClick={nextMonth}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedCalendar ? (
              <>
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {dayNames.map(day => (
                    <div key={day} className="text-center text-sm font-medium text-slate-600 py-2">
                      {day}
                    </div>
                  ))}
                  {getDaysInMonth(currentMonth).map((day, index) => {
                    const holiday = isHoliday(day);
                    return (
                      <div
                        key={index}
                        className={`min-h-[60px] p-1 text-center rounded border ${
                          day 
                            ? holiday 
                              ? 'bg-red-50 border-red-200' 
                              : 'bg-white border-slate-100 hover:border-indigo-200'
                            : 'bg-transparent border-transparent'
                        }`}
                      >
                        {day && (
                          <>
                            <span className={`text-sm ${holiday ? 'text-red-600 font-medium' : 'text-slate-700'}`}>
                              {day}
                            </span>
                            {holiday && (
                              <p className="text-xs text-red-600 truncate mt-1" title={holiday.name}>
                                {holiday.name}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Add Holiday Button */}
                {isAdmin() && (
                  <Dialog open={holidayDialogOpen} onOpenChange={setHolidayDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                        <Plus className="w-4 h-4 mr-2" />
                        {language === 'es' ? 'Añadir Festivo' : 'Add Holiday'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-['Manrope']">
                          {language === 'es' ? 'Nuevo Festivo' : 'New Holiday'}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleHolidaySubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label>{t('name')}</Label>
                          <Input
                            value={holidayForm.name}
                            onChange={(e) => setHolidayForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder={language === 'es' ? 'Ej: Navidad' : 'E.g.: Christmas'}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('date')}</Label>
                          <Input
                            type="date"
                            value={holidayForm.date}
                            onChange={(e) => setHolidayForm(prev => ({ ...prev, date: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={holidayForm.is_national}
                            onCheckedChange={(checked) => setHolidayForm(prev => ({ ...prev, is_national: checked }))}
                          />
                          <Label>{language === 'es' ? 'Festivo nacional' : 'National holiday'}</Label>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => setHolidayDialogOpen(false)}>
                            {t('cancel')}
                          </Button>
                          <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                            {t('save')}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}

                {/* BOE Import Button */}
                {isAdmin() && (
                  <Button 
                    variant="outline" 
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={handleOpenBoeDialog}
                    data-testid="import-boe-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Importar desde BOE' : 'Import from BOE'}
                  </Button>
                )}

                {/* Holidays List */}
                {holidays.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-slate-900 mb-3">
                      {language === 'es' ? 'Festivos en este calendario' : 'Holidays in this calendar'}
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('date')}</TableHead>
                          <TableHead>{t('name')}</TableHead>
                          <TableHead>{t('type')}</TableHead>
                          {isAdmin() && <TableHead className="text-right">{t('actions')}</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {holidays.sort((a, b) => a.date.localeCompare(b.date)).map(holiday => (
                          <TableRow key={holiday.id}>
                            <TableCell>{holiday.date}</TableCell>
                            <TableCell className="font-medium">{holiday.name}</TableCell>
                            <TableCell>
                              <Badge variant={holiday.is_national ? 'default' : 'secondary'}>
                                {holiday.is_national 
                                  ? (language === 'es' ? 'Nacional' : 'National')
                                  : (language === 'es' ? 'Local' : 'Local')
                                }
                              </Badge>
                            </TableCell>
                            {isAdmin() && (
                              <TableCell className="text-right">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleDeleteHoliday(holiday.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                {language === 'es' 
                  ? 'Selecciona un calendario de la lista para ver sus festivos'
                  : 'Select a calendar from the list to view its holidays'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* BOE Import Dialog */}
      <Dialog open={boeDialogOpen} onOpenChange={setBoeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-600" />
              {language === 'es' ? 'Importar Festivos del BOE' : 'Import BOE Holidays'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              {language === 'es' 
                ? 'Importa automáticamente los festivos nacionales y autonómicos de España desde el Boletín Oficial del Estado.'
                : 'Automatically import national and regional holidays from the Official State Gazette of Spain.'}
            </p>
            
            {/* Form */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Año' : 'Year'}</Label>
                <Select 
                  value={boeForm.year.toString()} 
                  onValueChange={(value) => {
                    setBoeForm(prev => ({ ...prev, year: parseInt(value) }));
                    setBoePreview(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Comunidad Autónoma' : 'Autonomous Community'}</Label>
                <Select 
                  value={boeForm.community_code || 'none'} 
                  onValueChange={(value) => {
                    setBoeForm(prev => ({ ...prev, community_code: value === 'none' ? '' : value }));
                    setBoePreview(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'es' ? 'Solo nacionales' : 'Nationals only'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {language === 'es' ? '-- Solo festivos nacionales --' : '-- National holidays only --'}
                    </SelectItem>
                    {communities.map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={boeForm.include_nationals}
                onCheckedChange={(checked) => {
                  setBoeForm(prev => ({ ...prev, include_nationals: checked }));
                  setBoePreview(null);
                }}
              />
              <Label>{language === 'es' ? 'Incluir festivos nacionales' : 'Include national holidays'}</Label>
            </div>
            
            <Button 
              variant="outline" 
              onClick={handleBoePreview} 
              disabled={boeLoading}
              className="w-full"
              data-testid="boe-preview-button"
            >
              {boeLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === 'es' ? 'Obteniendo festivos...' : 'Fetching holidays...'}
                </>
              ) : (
                <>
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Ver Vista Previa' : 'Preview Holidays'}
                </>
              )}
            </Button>
            
            {/* Preview */}
            {boePreview && (
              <div className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-900">
                    {language === 'es' ? 'Vista previa' : 'Preview'} ({boePreview.total} {language === 'es' ? 'festivos' : 'holidays'})
                  </h4>
                  {boePreview.community && (
                    <Badge className="bg-amber-100 text-amber-800">
                      {boePreview.community}
                    </Badge>
                  )}
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'es' ? 'Fecha' : 'Date'}</TableHead>
                        <TableHead>{language === 'es' ? 'Nombre' : 'Name'}</TableHead>
                        <TableHead>{language === 'es' ? 'Tipo' : 'Type'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {boePreview.holidays.map((h, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-sm">{h.date}</TableCell>
                          <TableCell>{h.name}</TableCell>
                          <TableCell>
                            <Badge variant={h.is_national ? 'default' : 'secondary'} className="text-xs">
                              {h.type}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                <div className="mt-4 flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setBoeDialogOpen(false)}
                  >
                    {t('cancel')}
                  </Button>
                  <Button 
                    className="bg-amber-600 hover:bg-amber-700"
                    onClick={handleBoeImport}
                    disabled={boeImporting}
                    data-testid="boe-import-confirm-button"
                  >
                    {boeImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {language === 'es' ? 'Importando...' : 'Importing...'}
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {language === 'es' ? `Importar ${boePreview.total} festivos` : `Import ${boePreview.total} holidays`}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calendars;
